import React, { useState, useEffect } from "react";
import { isEmpty } from "lodash";

import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Spinner,
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

//redux
import { useSelector, useDispatch } from "react-redux";

// File upload
import Dropzone from "react-dropzone";

import avatar from "../../assets/images/users/avatar-1.jpg";
// actions
import { resetProfileFlag } from "../../slices/thunks";
import { profileSuccess } from "../../slices/auth/profile/reducer";
import { createSelector } from "reselect";
import BreadCrumb from "../../Components/Common/BreadCrumb";
// API helper
import { APIClient } from "../../helpers/api_helper";

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

// Move selector outside component to maintain memoization
const selectLayoutState = (state:any) => state.Profile;
const userprofileData = createSelector(
  selectLayoutState,
  (state) => ({
    user: state.user,
    success: state.success,
    error: state.error
  })
);

const UserProfile = () => {
  const dispatch :any = useDispatch();

  const [email, setemail] = useState("");
  const [idx, setidx] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<any>([]);
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const [hasPassword, setHasPassword] = useState<boolean>(false);
  const [googleId, setGoogleId] = useState<string | null>(null);
  const [microsoftId, setMicrosoftId] = useState<string | null>(null);
  const [githubId, setGithubId] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);

  const apiClient = new APIClient();

  // API functions
  const fetchUserData = async () => {
    setLoading(true);
    setApiError(null);

    try {
      const response = await apiClient.get('/user/me');
      const userData = response.data || response;

      // Update state with fetched data
      if (userData) {
        // Set the user ID from the response
        const userId = userData.userId || userData.id || userData.user_id || userData._id;
        if (userId) {
          setidx(String(userId));
        }

        setDisplayName(userData.fullName || userData.name || userData.username || "");
        setemail(userData.email || "");

        // Set OAuth connection data
        setAuthProvider(userData.authProvider || null);
        setHasPassword(userData.hasPassword === true); // Use the hasPassword flag from backend
        setGoogleId(userData.googleId || null);
        setMicrosoftId(userData.microsoftId || null);
        setGithubId(userData.githubId || null);
        setCreatedAt(userData.createdAt || null);

        // Set avatar if available
        if (userData.avatar || userData.profileImage || userData.image) {
          setAvatarPreview(userData.avatar || userData.profileImage || userData.image);
        }

        // Reset form with new data
        validation.setValues({
          fullName: userData.fullName || userData.name || userData.username || "",
          email: userData.email || "",
          password: "",
          confirmPassword: "",
          idx: String(userId || ""),
        });
      }
    } catch (error: any) {
      console.error('Error fetching user data:', error);
      setApiError(error.response?.data?.message || 'Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const updateUserData = async (formData: any) => {
    if (!idx) return;
    
    setSaving(true);
    setApiError(null);
    setSuccessMessage(null);
    
    try {
      // Prepare form data for API
      const updateData = new FormData();
      updateData.append('fullName', formData.fullName);
      updateData.append('email', formData.email);
      
      // Only include password if it's provided and not empty
      if (formData.password && formData.password.trim() !== '') {
        updateData.append('password', formData.password);
      }
      
      if (selectedFiles.length > 0) {
        updateData.append('avatar', selectedFiles[0]);
      }
      
      const response = await apiClient.put(`/user/${idx}`, updateData);
      
      if (response) {
        setSuccessMessage('Profile updated successfully!');
        
        // Get the new avatar URL from response or use the preview URL
        let newAvatarUrl = null;
        const responseData = response.data || response;
        if (responseData?.avatar) {
          newAvatarUrl = responseData.avatar;
        } else if (responseData?.profileImage) {
          newAvatarUrl = responseData.profileImage;
        } else if (responseData?.image) {
          newAvatarUrl = responseData.image;
        } else if (selectedFiles.length > 0 && avatarPreview) {
          newAvatarUrl = avatarPreview;
        }
        
        // Update session storage with new user data
        const authUser = sessionStorage.getItem("authUser");
        if (authUser) {
          try {
            const parsed = JSON.parse(authUser);
            const updatedSession = {
              ...parsed,
              fullName: formData.fullName,
              email: formData.email,
            };
            
            // Update avatar in session if we have a new one
            if (newAvatarUrl) {
              updatedSession.avatar = newAvatarUrl;
              updatedSession.profileImage = newAvatarUrl;
              updatedSession.image = newAvatarUrl;
            }
            
            sessionStorage.setItem("authUser", JSON.stringify(updatedSession));
          } catch (error) {
            console.error('Error updating session:', error);
          }
        }
        
        // Update Redux state to trigger profile dropdown refresh
        dispatch(profileSuccess({
          status: 'Profile updated successfully',
          data: {
            fullName: formData.fullName,
            email: formData.email,
            avatar: newAvatarUrl,
            profileImage: newAvatarUrl,
            image: newAvatarUrl,
            name: formData.fullName,
            username: formData.fullName
          }
        }));
        
        // Update local state for immediate UI feedback
        setDisplayName(formData.fullName);
        setemail(formData.email);
        if (newAvatarUrl) {
          setAvatarPreview(newAvatarUrl);
        }
        
        // Clear password fields
        validation.setFieldValue('password', '');
        validation.setFieldValue('confirmPassword', '');
        
        // Clear selected files
        setSelectedFiles([]);
      }
    } catch (error: any) {
      console.error('Error updating user data:', error);
      setApiError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const resolveFullName = (payload : any): string | undefined => {
    if (!payload || typeof payload !== "object") return undefined;
    if (payload.fullName) return payload.fullName;
    if (payload.username) return payload.username;
    if (payload.name) return payload.name;
    if (payload.first_name && payload.last_name) return `${payload.first_name} ${payload.last_name}`.trim();
    if (payload.first_name) return payload.first_name;
    if (payload.data) return resolveFullName(payload.data);
    if (payload.user) return resolveFullName(payload.user);
    return undefined;
  };

  const resolveEmail = (payload : any): string | undefined => {
    if (!payload || typeof payload !== "object") return undefined;
    if (payload.email) return payload.email;
    if (payload.data) return resolveEmail(payload.data);
    if (payload.user) return resolveEmail(payload.user);
    return undefined;
  };

  const resolveId = (payload : any): string | undefined => {
    if (!payload || typeof payload !== "object") return undefined;
    if (payload._id) return payload._id;
    if (payload.id) return payload.id;
    if (payload.userId) return payload.userId;
    if (payload.user_id) return payload.user_id;
    if (payload.data) return resolveId(payload.data);
    return undefined;
  };

  const {
    user, success, error
  } = useSelector(userprofileData);

  // Fetch user data on component mount
  useEffect(() => {
    fetchUserData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Redux user updates
  useEffect(() => {
    if (!isEmpty(user)) {
      const nextName = resolveFullName(user);
      const nextEmail = resolveEmail(user);
      
      if (nextName) {
        setDisplayName(nextName);
      }
      if (nextEmail) {
        setemail(nextEmail);
      }

      // Update session storage
      const authUser = sessionStorage.getItem("authUser");
      if (authUser) {
        try {
          const parsed = JSON.parse(authUser);
          const updatedSession = { ...parsed };
          
          if (nextName) updatedSession.fullName = nextName;
          if (nextEmail) updatedSession.email = nextEmail;
          
          sessionStorage.setItem("authUser", JSON.stringify(updatedSession));
        } catch (error) {
          console.error('Error updating session:', error);
        }
      }
    }
    
    setTimeout(() => {
      dispatch(resetProfileFlag());
    }, 3000);
  }, [dispatch, user]);

  // Avatar upload handler
  const handleAcceptedFiles = (files: any) => {
    files.map((file: any) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setSelectedFiles(files);
    if (files.length > 0) {
      setAvatarPreview(files[0].preview);
    }
  };

  const validation :any= useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      fullName: displayName || 'Admin',
      email: email || '',
      password: '',
      confirmPassword: '',
      idx: idx || '',
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Please enter your full name"),
      email: Yup.string().email("Please enter a valid email address").required("Please enter your email"),
      password: Yup.string()
        .test('password-strength', 'Password must contain at least one uppercase letter, one lowercase letter, and one number, and must be a minimum of 8 characters long.', function(value) {
          if (!value || value.length === 0) {
            return true; // Optional field, so empty is valid
          }
          return PASSWORD_RULE.test(value);
        }),
      confirmPassword: Yup.string()
        .test('passwords-match', 'Passwords do not match', function(value) {
          const { password } = this.parent;
          if (!password || password.length === 0) {
            return true; // If no password provided, confirmPassword is not needed
          }
          if (!value || value.length === 0) {
            return this.createError({ message: 'Please confirm your password' });
          }
          return value === password;
        }),
    }),
    onSubmit: async (values) => {
      await updateUserData(values);
    }
  });

  document.title = "Profile | Velzon - React Admin & Dashboard Template";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Profile" pageTitle="Pages" />
          
          <Row>
            <Col lg="12">
              {error && error ? <Alert color="danger">{error}</Alert> : null}
              {success ? <Alert color="success">Profile updated successfully</Alert> : null}
              {apiError && <Alert color="danger">{apiError}</Alert>}
              {successMessage && <Alert color="success">{successMessage}</Alert>}

              {/* Account Type Info */}
              {!loading && authProvider && (
                <Alert color="info" className="mb-3">
                  <i className={`ri-${authProvider === 'GOOGLE' ? 'google' : authProvider === 'MICROSOFT' ? 'microsoft' : authProvider === 'GITHUB' ? 'github' : 'shield-check'}-fill me-2`}></i>
                  Account created via <strong>{authProvider === 'GOOGLE' ? 'Google' : authProvider === 'MICROSOFT' ? 'Microsoft' : authProvider === 'GITHUB' ? 'GitHub' : 'Email/Password'}</strong>
                  {createdAt && ` on ${new Date(createdAt).toLocaleDateString()}`}
                </Alert>
              )}

              {/* Connected Accounts Card */}
              {!loading && (
                <Card className="mb-3">
                  <CardBody>
                    <h5 className="card-title mb-3">
                      <i className="ri-link me-2"></i>Login Methods
                    </h5>
                    <p className="text-muted mb-3">Your connected authentication methods</p>
                    <Row className="g-3">
                      {/* Google Account - Only show if connected */}
                      {googleId && (
                        <Col lg={3} md={6}>
                          <div className="d-flex flex-column align-items-center justify-content-between p-3 border rounded h-100">
                            <div className="d-flex flex-column align-items-center text-center gap-2 mb-2">
                              <i className="ri-google-fill fs-1 text-danger"></i>
                              <div>
                                <div className="fw-medium">Google</div>
                                <small className="text-muted">
                                  {authProvider === 'GOOGLE' ? 'Primary login method' : 'Connected'}
                                </small>
                              </div>
                            </div>
                            <span className="badge bg-success">
                              <i className="ri-check-line me-1"></i>Connected
                            </span>
                          </div>
                        </Col>
                      )}

                      {/* Microsoft Account - Only show if connected */}
                      {microsoftId && (
                        <Col lg={3} md={6}>
                          <div className="d-flex flex-column align-items-center justify-content-between p-3 border rounded h-100">
                            <div className="d-flex flex-column align-items-center text-center gap-2 mb-2">
                              <i className="ri-microsoft-fill fs-1 text-primary"></i>
                              <div>
                                <div className="fw-medium">Microsoft</div>
                                <small className="text-muted">
                                  {authProvider === 'MICROSOFT' ? 'Primary login method' : 'Connected'}
                                </small>
                              </div>
                            </div>
                            <span className="badge bg-success">
                              <i className="ri-check-line me-1"></i>Connected
                            </span>
                          </div>
                        </Col>
                      )}

                      {/* GitHub Account - Only show if connected */}
                      {githubId && (
                        <Col lg={3} md={6}>
                          <div className="d-flex flex-column align-items-center justify-content-between p-3 border rounded h-100">
                            <div className="d-flex flex-column align-items-center text-center gap-2 mb-2">
                              <i className="ri-github-fill fs-1 text-dark"></i>
                              <div>
                                <div className="fw-medium">GitHub</div>
                                <small className="text-muted">
                                  {authProvider === 'GITHUB' ? 'Primary login method' : 'Connected'}
                                </small>
                              </div>
                            </div>
                            <span className="badge bg-success">
                              <i className="ri-check-line me-1"></i>Connected
                            </span>
                          </div>
                        </Col>
                      )}

                      {/* Email/Password - Always show as backup option */}
                      <Col lg={3} md={6}>
                        <div className="d-flex flex-column align-items-center justify-content-between p-3 border rounded h-100">
                          <div className="d-flex flex-column align-items-center text-center gap-2 mb-2">
                            <i className="ri-lock-password-fill fs-1 text-info"></i>
                            <div>
                              <div className="fw-medium">Email/Password</div>
                              <small className="text-muted">
                                {hasPassword ? (authProvider === 'EMAIL' ? 'Primary login method' : 'Backup access enabled') : 'Set a password for backup access'}
                              </small>
                            </div>
                          </div>
                          {hasPassword ? (
                            <span className="badge bg-success">
                              <i className="ri-check-line me-1"></i>Enabled
                            </span>
                          ) : (
                            <span className="badge bg-warning">
                              <i className="ri-alert-line me-1"></i>Not set
                            </span>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Helpful message if no password set */}
                    {!hasPassword && (googleId || microsoftId || githubId) && (
                      <Alert color="info" className="mt-3 mb-0">
                        <i className="ri-information-line me-2"></i>
                        <strong>Tip:</strong> Set a password to enable backup access in case you lose access to your {authProvider === 'GOOGLE' ? 'Google' : authProvider === 'MICROSOFT' ? 'Microsoft' : 'GitHub'} account.
                      </Alert>
                    )}
                  </CardBody>
                </Card>
              )}

              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Profile Information</h4>
                  
                  {loading ? (
                    <div className="text-center p-4">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading profile data...</p>
                    </div>
                  ) : (
                    <Form
                      className="form-horizontal"
                      onSubmit={(e:any) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                    <Row>
                      <Col lg={6}>
                        {/* Full Name */}
                        <div className="mb-3">
                          <Label className="form-label">Full Name <span className="text-danger">*</span></Label>
                          <Input
                            name="fullName"
                            className="form-control"
                            placeholder="Enter full name"
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.fullName || ""}
                            invalid={
                              validation.touched.fullName && validation.errors.fullName ? true : false
                            }
                          />
                          {validation.touched.fullName && validation.errors.fullName ? (
                            <FormFeedback type="invalid">{validation.errors.fullName}</FormFeedback>
                          ) : null}
                        </div>

                        {/* Email */}
                        <div className="mb-3">
                          <Label className="form-label">Email <span className="text-danger">*</span></Label>
                          <Input
                            name="email"
                            className="form-control"
                            placeholder="Enter email address"
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              validation.touched.email && validation.errors.email ? true : false
                            }
                          />
                          {validation.touched.email && validation.errors.email ? (
                            <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                          ) : null}
                        </div>

                        {/* Password Section - Contextual based on whether user has password */}
                        <div className="mb-3">
                          <Label className="form-label">
                            {hasPassword ? 'New Password' : 'Set Password'}
                            {!hasPassword && <span className="text-danger"> (Recommended for backup login)</span>}
                          </Label>
                          <Input
                            name="password"
                            type="password"
                            placeholder={hasPassword ? "Enter new password (leave blank to keep current)" : "Enter password"}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password || ""}
                            invalid={
                              validation.touched.password && validation.errors.password ? true : false
                            }
                          />
                          {validation.touched.password && validation.errors.password ? (
                            <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                          ) : null}
                          <small className="text-muted">
                            {hasPassword
                              ? 'Leave blank to keep current password. If changing, password must contain at least one uppercase letter, one lowercase letter, one number, and be at least 8 characters long.'
                              : 'Set a password to enable email/password login as a backup. Password must contain at least one uppercase letter, one lowercase letter, one number, and be at least 8 characters long.'
                            }
                          </small>
                        </div>

                        {/* Confirm Password */}
                        <div className="mb-3">
                          <Label className="form-label">
                            {hasPassword ? 'Confirm New Password' : 'Confirm Password'}
                          </Label>
                          <Input
                            name="confirmPassword"
                            type="password"
                            placeholder={hasPassword ? "Confirm new password" : "Confirm password"}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.confirmPassword || ""}
                            invalid={
                              validation.touched.confirmPassword && validation.errors.confirmPassword ? true : false
                            }
                          />
                          {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                            <FormFeedback type="invalid">{validation.errors.confirmPassword}</FormFeedback>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6}>
                        {/* Avatar Upload */}
                        <div className="mb-3">
                          <Label className="form-label">Avatar</Label>
                          <Dropzone
                            onDrop={acceptedFiles => {
                              handleAcceptedFiles(acceptedFiles);
                            }}
                            accept={{
                              'image/*': ['.jpeg', '.jpg', '.png', '.gif']
                            }}
                            maxFiles={1}
                          >
                            {({ getRootProps, getInputProps }) => (
                              <div className="dropzone dz-clickable" {...getRootProps()}>
                                <input {...getInputProps()} />
                                <div className="dz-message needsclick">
                                  <div className="mb-3">
                                    <i className="display-4 text-muted ri-upload-cloud-2-fill"></i>
                                  </div>
                                  <h5>Drop image here or click to upload.</h5>
                                  <p className="text-muted">
                                    Upload your profile picture (JPG, PNG, GIF)
                                  </p>
                                </div>
                              </div>
                            )}
                          </Dropzone>
                          
                          {/* Avatar Preview */}
                          {avatarPreview && (
                            <div className="mt-3">
                              <h6>Avatar Preview:</h6>
                              <img
                                src={avatarPreview}
                                alt="Avatar Preview"
                                className="avatar-lg rounded-circle img-thumbnail"
                              />
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Input name="idx" value={idx} type="hidden" />
                    
                    <div className="text-center mt-4">
                      <Button type="submit" color="primary" size="lg" disabled={saving}>
                        {saving && <Spinner size="sm" className="me-2">Loading...</Spinner>}
                        {saving ? 'Updating...' : 'Update Profile'}
                      </Button>
                    </div>
                  </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
